import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Box } from '@zillow/constellation-10';

import PersonaInquirySkeleton from './PersonaInquirySkeleton';

import type { InquiryError } from 'persona';

/**
 * Fixed, because nothing can size it to its content: `preferredDimensions` arrives empty for an
 * inline flow, `page-change`'s `pageHeight` reports a fraction of the rendered page, and the frame is
 * cross-origin so it cannot be measured from here. Detail and remaining options in ZRM-17119.
 */
const FRAME_HEIGHT = '800px';

/** An iframe with no width collapses to its ~300px intrinsic default. */
const FRAME_WIDTH = '100%';

const IFRAME_TITLE = 'Verify your identity';

/**
 * A widget iframe that fails to load reports neither `onReady` nor `onError`, so nothing but the
 * clock distinguishes it from a slow one. Long enough that a slow connection still wins the race.
 */
const READY_TIMEOUT_MS = 20_000;

export type PersonaInquiryProps = {
  inquiryId: string;
  sessionToken?: string;
  onComplete: () => void;
  onCancel: () => void;
  onError: (error: InquiryError | unknown) => void;
};

/**
 * Inline rather than in Persona's own modal, which rules out the `Client` class and leaves the
 * lower-level `setupIframe`/`setupEvents` pair. Wizard-agnostic so the mobile handoff page can use it.
 */
const PersonaInquiry = ({ inquiryId, sessionToken, onComplete, onCancel, onError }: PersonaInquiryProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  // Identifies this iframe in the SDK's postMessage channel, so two mounts can't cross wires.
  const containerId = useId();

  // Held in a ref so a parent re-render can't tear down and re-create the iframe mid-flow.
  const callbacksRef = useRef({ onComplete, onCancel, onError });
  callbacksRef.current = { onComplete, onCancel, onError };

  // Latched, so the timeout below cannot fire a second time over an error already reported.
  const reportFailure = useCallback((error: unknown) => {
    setHasFailed(true);
    callbacksRef.current.onError(error);
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let isCancelled = false;

    // A new inquiry loads from scratch, so a previous one's readiness must not carry over.
    setIsReady(false);
    setHasFailed(false);

    // Lazily: persona's UMD bundle reads `self` at module scope, which breaks the Next build.
    import('persona')
      .then(({ setupEvents, setupIframe }) => {
        if (isCancelled || !iframeRef.current) return;

        // Every callback must be present, null rather than undefined, per setupEvents' contract.
        unsubscribe = setupEvents(containerId, {
          onLoad: null,
          onReady: () => setIsReady(true),
          onEvent: null,
          onComplete: () => callbacksRef.current.onComplete(),
          onCancel: () => callbacksRef.current.onCancel(),
          onError: (error) => reportFailure(error),
          templateId: null,
          templateVersionId: null,
          host: null,
          eventsAllowlist: null,
        });

        setupIframe(iframeRef.current, containerId, 'inline', {
          inquiryId,
          sessionToken,
          iframeTitle: IFRAME_TITLE,
        });
        return undefined;
      })
      .catch((error: unknown) => {
        if (!isCancelled) reportFailure(error);
      });

    return () => {
      isCancelled = true;
      unsubscribe?.();
    };
  }, [inquiryId, sessionToken, containerId, reportFailure]);

  useEffect(() => {
    if (isReady || hasFailed) return undefined;

    const timer = setTimeout(() => {
      reportFailure(new Error(`Persona inquiry ${inquiryId} never became ready`));
    }, READY_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isReady, hasFailed, inquiryId, reportFailure]);

  return (
    <Box css={{ position: 'relative' }} style={{ height: FRAME_HEIGHT, width: FRAME_WIDTH }}>
      {/* setupIframe overwrites className, frameBorder, allow, sandbox, src and data-testid, so the
          title is the only attribute set here that survives. */}
      <iframe ref={iframeRef} title={IFRAME_TITLE} style={{ border: 'none', width: FRAME_WIDTH, height: '100%' }} />

      {/* Covers the frame rather than replacing it: setupIframe needs the element to stay mounted. */}
      {!isReady && (
        <Box
          css={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundColor: 'bg.neutral' }}
          aria-hidden={true}
        >
          <PersonaInquirySkeleton />
        </Box>
      )}
    </Box>
  );
};

export default PersonaInquiry;
